import * as Sentry from '@sentry/react';
import * as z from 'zod';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { SupportSnackbar } from '@/components/support-snackbar';
import { WarningAmberOutlined } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { getLocation } from '@/location/api/get-location';
import {
  MergeLocationParameters,
  mergeLocation,
} from '@/location/api/merge-location';
import { Location } from '@/location/api/types';
import { LocationSelectionList } from '@/location/components/location-selection-list';
import {
  LOCATION_FIELD_SCOPES,
  LOCATION_FIELD_SCOPE_LABELS,
  LocationField,
  appliesToLocationType,
} from '@/location/constants/merge-fields';
import {
  SparseCustomer,
  locationComponentsSchema,
  sparseCustomerSchema,
} from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  alpha,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactElement, Suspense, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

const mergeFormSchema = z.object({
  name: z.string(),
  customer: sparseCustomerSchema.nullable(),
  address: locationComponentsSchema.nullable(),
  isInventory: z.boolean(),
  isTransient: z.boolean(),
  description: z.string().nullable(),
  consignee: sparseCustomerSchema.nullable(),
});

type MergeFormData = z.infer<typeof mergeFormSchema>;

type MergeFieldPath = keyof MergeFormData & keyof Location;

interface Props {
  isOpen: boolean;
  location: Location;
  onClose: () => void;
}

interface changeWarningData {
  retainedIDLabel: string;
  removedIDLabel: string;
  retainedFieldLabel: string;
  removedFieldLabel: string;
}

interface FieldDefinition {
  label: string;
  path: MergeFieldPath;
  required: boolean;
  fieldType: 'sparse-entity' | 'string' | 'boolean';
  /** Keys into the shared site/zone taxonomy the audit renders from too */
  field: LocationField;
  changeWarning?: (data: changeWarningData) => ReactElement;
}

// Every merge shows every field. A zone computes its customer from the parent site, reads that
// site's address, and cannot be transient; a site has no description or consignee of its own.
const mergeFields: FieldDefinition[] = [
  {
    label: 'Name',
    path: 'name',
    required: true,
    fieldType: 'string',
    field: 'name',
  },
  {
    label: 'Is inventory',
    path: 'isInventory',
    required: true,
    fieldType: 'boolean',
    field: 'isInventory',
    changeWarning: (data) => (
      <ConditionalTooltip
        arrow
        tooltipText={`Changing the inventory status for locations with asset activity will affect historical reporting. If you want to preserve historical inventory and non-inventory reporting, you should move assets to ${data.retainedIDLabel} and then archive ${data.removedIDLabel}`}
      >
        <WarningAmberOutlined color="warning" fontSize="small" />
      </ConditionalTooltip>
    ),
  },
  {
    label: 'Company',
    path: 'customer',
    required: false,
    fieldType: 'sparse-entity',
    field: 'company',
  },
  {
    label: 'Address',
    path: 'address',
    required: false,
    fieldType: 'string',
    field: 'address',
  },
  {
    label: 'Is transient',
    path: 'isTransient',
    required: true,
    fieldType: 'boolean',
    field: 'isTransient',
  },
  {
    label: 'Description',
    path: 'description',
    required: false,
    fieldType: 'string',
    field: 'description',
  },
  {
    label: 'Consignee',
    path: 'consignee',
    required: false,
    fieldType: 'sparse-entity',
    field: 'consignee',
  },
];

const MERGE_INTO_OWN_ZONE_ERROR_CODE =
  ERROR_CODE_MAPPING.location_merge_into_own_zone;
const NAME_CONFLICT_ERROR_CODES: number[] = [
  ERROR_CODE_MAPPING.location_duplicate_name,
  ERROR_CODE_MAPPING.zone_name_matches_parent,
  ERROR_CODE_MAPPING.site_name_matches_zone,
];

const OWN_SITE_MERGE_MESSAGE = "Zones can't be merged with their own site";

const steps = ['Select location to merge', 'Select data fields to merge'];

function appliesTo(field: FieldDefinition, locationType: Location['type']) {
  return appliesToLocationType(field.field, locationType);
}

// Heads a column, chipped when the record it stands for is a zone so the kinds are told apart
// at a glance
function ColumnHeader(props: { label: string; isZone: boolean }) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="subtitle2">{props.label}</Typography>
      {props.isZone && <Chip size="small" label="Zone" variant="outlined" />}
    </Stack>
  );
}

// Muted small-caps marker beside a field label, matching the admin-only label in the user list
function ScopeLabel(props: { text: string }) {
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      display="inline"
      sx={{
        textTransform: 'lowercase',
        fontVariant: 'small-caps',
        marginLeft: 1,
      }}
    >
      {props.text}
    </Typography>
  );
}

interface SelectLocationStepProps {
  location: Location;
  onLocationSelect: (locationKey: string) => void;
  blockedLocation?: { locationKey: string; reason: string };
}

function SelectLocationStep(props: SelectLocationStepProps) {
  return (
    <Stack spacing={3}>
      <Grid container sx={{ px: 8 }}>
        <Grid item xs={6} sx={{ display: 'flex', pr: 2 }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Typography variant="h6">{props.location.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {props.location.address?.formattedAddress ?? 'No address'}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Current associations
                </Typography>
                <Stack gap={1} direction="column" sx={{ flexGrow: 1 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.location.activeProjectCount)}
                    </Typography>
                    <Typography variant="body2">active projects</Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.location.assetCount)}
                    </Typography>
                    <Typography variant="body2">assets on location</Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.location.zoneCount)}
                    </Typography>
                    <Typography variant="body2">zones</Typography>
                  </Paper>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', pl: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" gutterBottom>
              Select a location or zone to merge with {props.location.name}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Suspense
                fallback={
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                }
              >
                <LocationSelectionList
                  onLocationSelect={props.onLocationSelect}
                  excludeLocationId={props.location.id}
                  blocked={props.blockedLocation}
                />
              </Suspense>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
}

interface CompareAndMergeStepProps {
  primaryLocation: Location;
  selectedLocation: Location;
  fieldSelections: Partial<Record<MergeFieldPath, 'primary' | 'selected'>>;
  onFieldSelectionChange: (
    fieldPath: MergeFieldPath,
    source: 'primary' | 'selected'
  ) => void;
  watch: (path: MergeFieldPath) => MergeFormData[MergeFieldPath];
}

// Values sit beside a radio, so they keep a matching indent on the rows that have none. A
// read-only comparison has no radios anywhere and drops the indent entirely.
function ValueCell(props: {
  value: string | null;
  hasRadioColumn: boolean;
  notSetLabel?: string;
}) {
  return (
    <Stack direction="row" alignItems="center">
      {props.hasRadioColumn && <Box sx={{ p: 2 }}></Box>}
      {props.value === null ? (
        <Typography variant="body2" color="text.secondary">
          {props.notSetLabel ?? 'Not set'}
        </Typography>
      ) : (
        <Typography variant="body2">{props.value}</Typography>
      )}
    </Stack>
  );
}

type FieldValue =
  | string
  | null
  | number
  | boolean
  | SparseCustomer
  | { formattedAddress: string | null };

const getFieldValue = (field: FieldDefinition, val: FieldValue) => {
  if (val === null) return null;
  if (field.fieldType === 'sparse-entity') {
    return (val as SparseCustomer).name;
  }
  if (field.fieldType === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  if (
    field.path === 'address' &&
    typeof val === 'object' &&
    val !== null &&
    'formattedAddress' in val
  ) {
    return (val as { formattedAddress: string | null }).formattedAddress;
  }
  return val as string;
};

function CompareAndMergeStep(props: CompareAndMergeStepProps) {
  // Only a field both records carry can be chosen between
  const showRadioFor = (field: FieldDefinition) => {
    if (
      !appliesTo(field, props.primaryLocation.type) ||
      !appliesTo(field, props.selectedLocation.type)
    ) {
      return false;
    }

    const primaryValue = getFieldValue(
      field,
      props.primaryLocation[field.path]
    );
    const selectedValue = getFieldValue(
      field,
      props.selectedLocation[field.path]
    );

    return (
      (Boolean(primaryValue) || Boolean(selectedValue)) &&
      primaryValue !== selectedValue
    );
  };

  // A merge between two records of the same kind has no need to name the kind, so the scope
  // labels and the other kind's fields only appear when the two differ
  const isTypeChange =
    props.primaryLocation.type !== props.selectedLocation.type;
  const visibleFields = isTypeChange
    ? mergeFields
    : mergeFields.filter((field) =>
        appliesTo(field, props.primaryLocation.type)
      );

  // Values indent to clear the radio column, so they only indent when a radio is on show
  const hasRadioColumn = visibleFields.some(showRadioFor);

  return (
    <Paper variant="outlined" sx={{ overflow: 'auto', flexGrow: 1, pb: 1 }}>
      <Stack>
        <Box
          display="grid"
          gridTemplateColumns="2fr 2fr 2fr 3fr"
          gap={2}
          sx={{ px: 2, py: 1.5 }}
          alignItems="center"
        >
          <Typography variant="subtitle2">Field</Typography>
          <ColumnHeader
            label={props.primaryLocation.name}
            isZone={props.primaryLocation.type === 'ZONE'}
          />
          <ColumnHeader
            label={props.selectedLocation.name}
            isZone={props.selectedLocation.type === 'ZONE'}
          />
          {/* The merged record is the retained one, so it keeps that record's kind */}
          <ColumnHeader
            label="Merged result"
            isZone={props.primaryLocation.type === 'ZONE'}
          />
        </Box>

        <Divider sx={{ mb: 1 }} />

        {visibleFields.map((field) => {
          const primaryValue = getFieldValue(
            field,
            props.primaryLocation[field.path]
          );
          const selectedValue = getFieldValue(
            field,
            props.selectedLocation[field.path]
          );
          const selection = props.fieldSelections[field.path];
          const showRadio = showRadioFor(field);
          const scopeCaption = isTypeChange
            ? LOCATION_FIELD_SCOPE_LABELS[LOCATION_FIELD_SCOPES[field.field]]
            : null;
          const fieldValue = getFieldValue(field, props.watch(field.path));
          return (
            <Box key={field.path}>
              <Box
                display="grid"
                sx={{ px: 2 }}
                gridTemplateColumns="2fr 2fr 2fr 3fr"
                gap={2}
                alignItems="center"
              >
                <Typography variant="body2">
                  {field.label}
                  {field.required && <span style={{ color: 'red' }}>*</span>}
                  {scopeCaption !== null && <ScopeLabel text={scopeCaption} />}
                </Typography>
                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'primary'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'primary')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {primaryValue ?? (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : appliesTo(field, props.primaryLocation.type) ? (
                    <ValueCell
                      value={primaryValue}
                      hasRadioColumn={hasRadioColumn}
                    />
                  ) : (
                    <ValueCell
                      value={null}
                      notSetLabel="Not applicable"
                      hasRadioColumn={hasRadioColumn}
                    />
                  )}
                </Box>

                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'selected'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'selected')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {selectedValue ?? (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : appliesTo(field, props.selectedLocation.type) ? (
                    <ValueCell
                      value={selectedValue}
                      hasRadioColumn={hasRadioColumn}
                    />
                  ) : (
                    <ValueCell
                      value={null}
                      notSetLabel="Not applicable"
                      hasRadioColumn={hasRadioColumn}
                    />
                  )}
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="body2" color="text.primary">
                    {!appliesTo(field, props.primaryLocation.type) ? (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Not applicable
                      </Typography>
                    ) : (
                      fieldValue || (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          Not set
                        </Typography>
                      )
                    )}
                  </Typography>
                  {fieldValue &&
                    primaryValue != selectedValue &&
                    field.changeWarning !== undefined &&
                    field.changeWarning({
                      retainedFieldLabel: primaryValue ?? 'Not set',
                      removedFieldLabel: selectedValue ?? 'Not set',
                      retainedIDLabel: props.primaryLocation.name,
                      removedIDLabel: props.selectedLocation.name,
                    })}
                </Stack>
              </Box>
              {visibleFields[visibleFields.length - 1].path !== field.path && (
                <Divider sx={{ gridColumn: '1/-1', my: 1 }} />
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export function MergeLocationDialog(props: Props) {
  const shouldInvalidate = useRef(false);
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const { showToast } = useToast();
  const [selectedLocationKey, setSelectedLocationKey] = useState<string | null>(
    null
  );
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [fieldSelections, setFieldSelections] = useState<
    Partial<Record<MergeFieldPath, 'primary' | 'selected'>>
  >({});
  const seededForKey = useRef<string | null>(null);

  const { data: selectedLocationData } = useQuery({
    queryKey: ['location', selectedLocationKey],
    queryFn: () => getLocation(selectedLocationKey!),
    enabled: !!selectedLocationKey,
  });

  const selectedLocation = selectedLocationData?.location;

  const isZoneRetained = props.location.type === 'ZONE';

  // A zone's parent site is on the record already, so the row is disabled rather than letting
  // the server reject the merge
  const parentSiteKey = props.location.parentLocation?.key;
  const blockedLocation =
    isZoneRetained && parentSiteKey !== undefined
      ? { locationKey: parentSiteKey, reason: OWN_SITE_MERGE_MESSAGE }
      : undefined;
  const retainedDefaults = {
    name: props.location.name,
    customer: props.location.customer,
    address: props.location.address,
    isInventory: props.location.isInventory,
    isTransient: props.location.isTransient,
    description: props.location.description,
    consignee: props.location.consignee,
  };

  const { handleSubmit, setValue, watch, reset } = useForm<MergeFormData>({
    resolver: zodResolver(mergeFormSchema),
    defaultValues: retainedDefaults,
  });

  const mergeLocationMutation = useMutation({
    mutationFn: (parameters: MergeLocationParameters) =>
      mergeLocation(props.location.key, props.location.type, parameters),
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode === MERGE_INTO_OWN_ZONE_ERROR_CODE) {
        setMergeError(OWN_SITE_MERGE_MESSAGE);
        setActiveStep(0);
        return;
      }
      if (
        apiError.apiStatusCode !== undefined &&
        NAME_CONFLICT_ERROR_CODES.includes(apiError.apiStatusCode)
      ) {
        setNameError(apiError.message);
        return;
      }
      Sentry.captureException(error);
    },
    onSuccess: () => {
      shouldInvalidate.current = true;
      showToast({
        duration: 5000,
        element: <Box>Merge complete, now viewing the merged record</Box>,
      });
      handleClose();
    },
  });

  // This needs to be run on success when the component unmounts to avoid it reloading a location that doesn't exist
  useEffect(() => {
    return () => {
      if (shouldInvalidate.current) {
        queryClient.invalidateQueries();
        shouldInvalidate.current = false;
      }
    };
  }, [queryClient]);

  const handleMergeSubmit = (data: MergeFormData) => {
    setNameError(null);

    if (isZoneRetained) {
      return mergeLocationMutation.mutate({
        removedLocationId: selectedLocation?.id!,
        name: data.name,
        description: data.description,
        consignee: data.consignee?.id ?? null,
        isInventory: data.isInventory,
        // Taking the other record's inventory value pins it, otherwise the zone keeps
        // whatever inherit or override state it already had
        ...(data.isInventory !== props.location.isInventory
          ? { isInventoryOverride: true }
          : {}),
      });
    }

    return mergeLocationMutation.mutate({
      removedLocationId: selectedLocation?.id!,
      name: data.name,
      customerId: data.customer?.id ?? null,
      isInventory: data.isInventory,
      address: data.address ?? null,
      isTransient: data.isTransient,
    });
  };

  useEffect(() => {
    if (selectedLocation && selectedLocationKey) {
      // Seeding overwrites the radios, so it runs once per selection rather than on every
      // refetch of the same record
      if (seededForKey.current === selectedLocationKey) return;
      seededForKey.current = selectedLocationKey;

      setMergeError(null);

      const initialSelections: Partial<
        Record<MergeFieldPath, 'primary' | 'selected'>
      > = {};

      // Only the fields both records carry can be chosen between, so only those get a
      // starting selection
      const selectableFields = mergeFields.filter(
        (field) =>
          appliesTo(field, props.location.type) &&
          appliesTo(field, selectedLocation.type)
      );

      selectableFields.forEach((field) => {
        const primaryValue = props.location[field.path];
        const selectedValue = selectedLocation[field.path];

        // Block numbers that can come back from counts that are currently not able to be set
        // This causes a type error and thus needs to be blocked
        if (
          typeof primaryValue === 'number' ||
          typeof selectedValue === 'number'
        )
          return;

        if (primaryValue !== null) {
          initialSelections[field.path] = 'primary';
          setValue(field.path, primaryValue);
        } else if (selectedValue !== null) {
          initialSelections[field.path] = 'selected';
          setValue(field.path, selectedValue);
        }
      });
      setFieldSelections(initialSelections);
      setActiveStep(1);
    }
  }, [selectedLocation, selectedLocationKey, props.location, setValue]);

  const handleFieldSelectionChange = (
    fieldPath: MergeFieldPath,
    source: 'primary' | 'selected'
  ) => {
    setFieldSelections((prev) => ({ ...prev, [fieldPath]: source }));

    const sourceLocation =
      source === 'primary' ? props.location : selectedLocation;

    // Bail if no source location
    if (!sourceLocation) return;

    const value = sourceLocation[fieldPath];

    // The schema for location can have numbers, however this doesn't have numbers correctly in the post data
    // If numbers are going to be in the post data in the future, remove this and it will work
    if (typeof value === 'number') return;

    setValue(fieldPath, value);
  };

  // Abandoning a selection has to drop everything it wrote, or a value chosen against a record
  // that is no longer part of the merge is submitted with the next one
  const clearSelection = () => {
    setActiveStep(0);
    setSelectedLocationKey(null);
    setMergeError(null);
    setNameError(null);
    setFieldSelections({});
    seededForKey.current = null;
    reset(retainedDefaults);
    mergeLocationMutation.reset();
  };

  const handleClose = () => {
    clearSelection();
    props.onClose();
  };

  const handleBack = clearSelection;

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <SupportSnackbar
        isOpen={mergeLocationMutation.isError && !mergeError && !nameError}
        message="An error occurred while merging the locations."
        onClose={mergeLocationMutation.reset}
      />
      <DialogTitle>
        Merge locations
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select another location or zone to merge into {props.location.name}
        </Typography>
      </DialogTitle>
      {mergeError && (
        <Box sx={{ px: 6, pb: 2 }}>
          <Alert severity="error">{mergeError}</Alert>
        </Box>
      )}
      <Box sx={{ px: 6 }}>
        <Alert severity="warning">
          <Typography
            variant="inherit"
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {props.location.name} will be preserved
            </Typography>{' '}
            and the
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {' '}
              other record will be permanently deleted
            </Typography>
            .
          </Typography>{' '}
          You will select which information you want to keep (name, address,
          etc). All other associations will be migrated automatically (customer,
          projects, assets, etc).
        </Alert>
      </Box>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper
            alternativeLabel
            activeStep={activeStep}
            sx={{
              '& .MuiStepIcon-root': {
                fontSize: '2.25rem', // 1.5x the default size (1.5rem * 1.5 = 2.25rem)
              },
              '& .MuiStepIcon-text': {
                fontWeight: 'bold',
              },
              '& .MuiStepConnector-line': {
                borderTopWidth: '1px', // Make the line slightly thicker to match larger icons
              },
              '& .MuiStepConnector-root': {
                top: '18px', // Adjust to align with center of enlarged icons (2.25rem / 2 = 1.125rem ≈ 18px)
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {activeStep === 0 && (
          <SelectLocationStep
            location={props.location}
            onLocationSelect={setSelectedLocationKey}
            blockedLocation={blockedLocation}
          />
        )}

        {activeStep === 1 && selectedLocation && (
          <form onSubmit={handleSubmit(handleMergeSubmit)}>
            <Stack spacing={2}>
              <CompareAndMergeStep
                primaryLocation={props.location}
                selectedLocation={selectedLocation}
                fieldSelections={fieldSelections}
                onFieldSelectionChange={handleFieldSelectionChange}
                watch={watch}
              />
              {nameError && <Alert severity="error">{nameError}</Alert>}
            </Stack>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', m: 1 }}>
        {activeStep === 0 ? (
          <Button onClick={handleClose}>Cancel</Button>
        ) : (
          <>
            <Button onClick={handleBack}>Back</Button>
            <LoadingButton
              onClick={handleSubmit(handleMergeSubmit)}
              variant="contained"
              loading={mergeLocationMutation.isPending}
            >
              Confirm merge
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
